import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import InputField from '../../components/forms/InputField';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import MyReferralCode from '../../components/common/MyReferralCode';
import { apiRequest, handleBranding, formatDate } from '../../utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Shared "Refer a friend" compose page (/refer), reached from the bottom of every
   home (HT / PLG Home.js + Bravos MemberHome.js).

   The shareable link + copy/WhatsApp are shown IMMEDIATELY (email can be a barrier),
   over the event's shop banner. Entering a friend's email is an OPTIONAL extra that
   also emails them the same link. The link carries the referrer + event, so it works
   before any email is typed. */
const ReferFriend = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        setInfo(await apiRequest('GET', '/refer'));
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const eventName = info?.event_name || '';
  const shareLink = info?.share_link || '';
  const shareText = t('referShareText', { event: eventName });
  const meta = info
    ? [info.event_date ? formatDate(info.event_date) : null, info.location_name]
      .filter(Boolean).join(' · ')
    : '';

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
    } catch { window.prompt(t('copyLink'), text); }
  };

  const share = async () => {
    if (!shareLink) return;
    const payload = { title: t('referFriend'), text: shareText, url: shareLink };
    if (navigator.share) {
      try { await navigator.share(payload); return; } catch { /* fall through to copy */ }
    }
    copy(`${shareText}\n${shareLink}`);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) { setError(t('invalidEmail')); return; }
    setSubmitting(true); setError(null);
    try {
      await apiRequest('POST', '/refer', { email: email.trim() });
      setSent(true); setEmail('');
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <Screen p="3">
      <div className="top" style={{ width: '100%', maxWidth: '560px', margin: '0 auto' }}>
        <div className="text-start mb-2">
          <button type="button" className="home-pill" onClick={() => navigate('/home')}>
            <Icon name="arrow-left" /> <span>{t('back', { defaultValue: 'Back' })}</span>
          </button>
        </div>

        <Card p="0">
          {info?.event_image && (
            <img src={info.event_image} alt={eventName}
                 style={{ width: '100%', height: '150px', objectFit: 'cover',
                          borderTopLeftRadius: '.5rem', borderTopRightRadius: '.5rem' }} />
          )}
          <div className="p-3">
            <Title heading="3" my="0" className="mb-1">{t('referFriend')}</Title>
            {meta && <div className="text-muted small mb-2">{eventName}{meta ? ` · ${meta}` : ''}</div>}
            <p className="text-secondary mb-3">{t('referFriendIntro', { event: eventName })}</p>

            {/* A PLG / HT player shares their own permanent code instead of the 30-day link:
                it keeps working after the event, when friends claim a spot on the early list. */}
            {info?.my_code ? (
              <MyReferralCode code={info.my_code} brand={eventName} />
            ) : (
            <>
            {/* Share the link right away — no email needed. */}
            <div className="text-uppercase small text-muted fw-semibold mb-1">{t('shareThisLink')}</div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input className="home-invite__input" readOnly value={shareLink}
                     onFocus={(e) => e.target.select()} style={{ flex: 1 }} />
              <button type="button" className="home-pill" onClick={() => copy(`${shareText}\n${shareLink}`)}>
                <Icon name="copy" />
                <span>{copied ? t('copied', { defaultValue: 'Copied!' }) : t('copyLink')}</span>
              </button>
            </div>
            <Button type="button" onClick={share}>
              <Icon name="share-square" /> {t('shareLink')}
            </Button>
            </>
            )}

            <hr className="my-3" />

            {/* Optional: also email the friend directly. */}
            <p className="text-secondary small text-start mb-2">{t('orSendByEmail')}</p>
            <form onSubmit={submitEmail}>
              <InputField
                type="email"
                name="friendEmail"
                label={t('friendEmailOptional')}
                placeholder="friend@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(null); if (sent) setSent(false); }}
                maxLength={120}
              />
              {error && <p className="text-danger text-start mb-2">{error}</p>}
              {sent && <p className="text-success text-start mb-2"><Icon name="check" /> {t('referralSent')}</p>}
              <Button type="submit" disabled={submitting || !email.trim()}>
                {submitting ? t('pleaseWait') : t('sendReferral')}
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </Screen>
  );
};

export default ReferFriend;
